import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { Portal } from '@/components/shared/Portal';
import { cn } from '@/utils/cn';

type FloatingPlacement = 'top' | 'bottom';

interface FloatingLayerRenderState {
  placement: FloatingPlacement;
  maxHeight: number;
}

interface FloatingLayerProps {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  id?: string;
  className?: string;
  offset?: number;
  viewportPadding?: number;
  zIndex?: number;
  matchAnchorWidth?: boolean;
  onDismiss?: () => void;
  children: ReactNode | ((state: FloatingLayerRenderState) => ReactNode);
}

interface FloatingLayout extends FloatingLayerRenderState {
  top: number;
  left: number;
  width?: number;
  maxWidth: number;
}

const DEFAULT_OFFSET = 10;
const DEFAULT_VIEWPORT_PADDING = 12;
const DEFAULT_Z_INDEX = 140;
const MIN_FLOATING_HEIGHT = 80;
const PREFERRED_VISIBLE_HEIGHT = 280;

function clamp(value: number, minimum: number, maximum: number): number {
  if (maximum < minimum) {
    return minimum;
  }

  return Math.min(Math.max(value, minimum), maximum);
}

export function FloatingLayer({
  open,
  anchorRef,
  id,
  className,
  offset = DEFAULT_OFFSET,
  viewportPadding = DEFAULT_VIEWPORT_PADDING,
  zIndex = DEFAULT_Z_INDEX,
  matchAnchorWidth = false,
  onDismiss,
  children,
}: FloatingLayerProps): JSX.Element | null {
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<FloatingLayout | null>(null);

  useEffect(() => {
    if (!open) {
      setLayout(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !onDismiss) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      if (anchorRef.current?.contains(target) || floatingRef.current?.contains(target)) {
        return;
      }

      onDismiss();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchorRef, onDismiss, open]);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    let frameId = 0;
    let resizeObserver: ResizeObserver | null = null;

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const floating = floatingRef.current;

      if (!anchor || !floating) {
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const roomBelow = viewportHeight - anchorRect.bottom - viewportPadding;
      const roomAbove = anchorRect.top - viewportPadding;
      const visibleHeightTarget = Math.min(floating.offsetHeight || PREFERRED_VISIBLE_HEIGHT, PREFERRED_VISIBLE_HEIGHT);
      const placement: FloatingPlacement =
        roomBelow >= visibleHeightTarget || roomBelow >= roomAbove ? 'bottom' : 'top';
      const availableHeight = Math.min(
        Math.max(MIN_FLOATING_HEIGHT, (placement === 'bottom' ? roomBelow : roomAbove) - offset),
        viewportHeight - viewportPadding * 2,
      );
      const maxWidth = Math.max(240, viewportWidth - viewportPadding * 2);
      const width = matchAnchorWidth ? Math.min(anchorRect.width, maxWidth) : undefined;
      const floatingWidth = width ?? Math.min(floating.offsetWidth || anchorRect.width, maxWidth);
      const floatingHeight = Math.min(floating.offsetHeight || availableHeight, availableHeight);
      const left = clamp(anchorRect.left, viewportPadding, viewportWidth - viewportPadding - floatingWidth);
      const top =
        placement === 'bottom'
          ? clamp(anchorRect.bottom + offset, viewportPadding, viewportHeight - viewportPadding - floatingHeight)
          : clamp(anchorRect.top - offset - floatingHeight, viewportPadding, viewportHeight - viewportPadding - floatingHeight);

      setLayout({
        top,
        left,
        width,
        maxWidth,
        maxHeight: availableHeight,
        placement,
      });
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updatePosition);
    };

    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);

      if (anchorRef.current) {
        resizeObserver.observe(anchorRef.current);
      }

      if (floatingRef.current) {
        resizeObserver.observe(floatingRef.current);
      }
    }

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
      resizeObserver?.disconnect();
    };
  }, [anchorRef, matchAnchorWidth, offset, open, viewportPadding]);

  if (!open) {
    return null;
  }

  const nextStyle: CSSProperties = {
    top: layout?.top ?? 0,
    left: layout?.left ?? 0,
    width: layout?.width,
    maxWidth: layout?.maxWidth,
    maxHeight: layout?.maxHeight,
    zIndex,
    visibility: layout ? 'visible' : 'hidden',
  };

  const renderState: FloatingLayerRenderState = {
    placement: layout?.placement ?? 'bottom',
    maxHeight: layout?.maxHeight ?? PREFERRED_VISIBLE_HEIGHT,
  };

  return (
    <Portal>
      <div
        ref={floatingRef}
        id={id}
        className={cn('floating-layer', `floating-layer--${renderState.placement}`, className)}
        style={nextStyle}
      >
        {typeof children === 'function' ? children(renderState) : children}
      </div>
    </Portal>
  );
}
